import { Component, OnInit, effect, inject, untracked, DestroyRef } from '@angular/core';
import { NavigationService } from '../../services/navigation.service';
import { IMenuItem, MenuItemTypes } from '../../models/menu-items';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { NavigationFacade } from '../../state/navigation/facades/navigation-facade';
import { filter, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LabFacade } from '../../../views/labs/facade/lab.facade';
import { DialogService } from '../../services/dialog.service';
import { ILab } from '../../../views/labs/models/Labs';
import { ProjectFacade } from '../../../views/projects/facades/project.facade';
import { IProject } from '../../../views/projects/models/Project';
import { AuthenticationFacade } from '../../../views/authentication/facade/authentication.facade';
import { labSectionLink, isLabSectionActive } from '../../utils/lab-scoped-nav';

@Component({
  selector: 'app-sidenav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.css'],
  imports: [RouterModule, CommonModule],
})
export class SidenavComponent implements OnInit {
  // Dependencies
  private readonly navigationService = inject(NavigationService);
  private readonly navigationFacade = inject(NavigationFacade);
  private readonly router = inject(Router);
  private readonly labFacade = inject(LabFacade);
  private readonly dialogService = inject(DialogService);
  private readonly projectFacade = inject(ProjectFacade);
  private readonly authFacade = inject(AuthenticationFacade);
  private readonly destroyRef = inject(DestroyRef);

  // State
  sideNavMenuItems: IMenuItem[] = [];
  selectedMenuItem: IMenuItem | null = null;
  expandedMenu: { [key: string]: boolean } = {};
  currentRoute = '';
  selectedLabId: number | null = null;
  allProjects: IProject[] = [];

  // Constants
  readonly menuItemTypes = MenuItemTypes;
  readonly menuIconMap: { [key: string]: string } = {
    Dashboard: '/icons/report-columns (1).svg',
    Home: '/icons/home.svg',
    'Lab details': '/icons/info-empty.svg',
    'Dataset Requests': '/icons/icon _ request file.svg',
    'My Dataset Requests': '/icons/icon _ request file.svg',
    'Archive Requests': '/icons/lu-archive.svg',
    'My Archive Requests': '/icons/lu-archive.svg',
    'My Metadata Requests': '/icons/icons_bi-list-ul.svg',
    Labs: '/icons/Flask Fill-2.svg',
    Projects: '/icons/hi-outline-square3stack3d-2.svg',
    Sequences: '/icons/master-file.svg',
    'Draft Sequences': '/icons/master-file.svg',
    'Batch Endpoints': '/icons/cloud-upload.svg',
    'Lab Users': '/icons/users.svg',
    Administration: '/icons/hi-outline-cog8tooth (1).svg',
    Admin: '/icons/hi-outline-cog8tooth (1).svg',
    'Data Catalog': '/icons/Icons _ db (1).svg',
    'Data Explorer': '/icons/bar-chart.svg',
    'Catalog Overview': '/icons/view.svg',
    Pipelines: '/icons/pipeline.svg',
  };

  getMenuIcon(menuName: string): string | null {
    return this.menuIconMap[menuName] || null;
  }

  // Lifecycle
  private scrollTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastScrolledLabState: string | null = null;

  private sessionDataFetched = false;

  constructor() {
    this.setupEffects();
    this.fetchSessionDataOnceAuthenticated();
  }

  // Read reactively rather than once in ngOnInit. The user signal is null
  // while GetAuthUser is in flight, and it used to be non-null only because a
  // previous account's user had been rehydrated from localStorage. Now that a
  // session change genuinely empties the store, a one-shot read would skip
  // these fetches and leave the nav with no labs.
  private fetchSessionDataOnceAuthenticated(): void {
    effect(() => {
      if (!this.authFacade.userSignal() || this.sessionDataFetched) {
        return;
      }
      this.sessionDataFetched = true;
      this.labFacade.getLabs();
      this.projectFacade.getProjectsList();
    });
  }

  ngOnInit(): void {
    this.initializeMenuItems();
    this.setupRouteSubscriptions();
    this.setupNavigationFacadeSubscription();

    // Clean up scroll timeout on destroy
    this.destroyRef.onDestroy(() => {
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
    });
  }

  private initializeMenuItems(): void {
    this.sideNavMenuItems = this.navigationService.getAllMenuItems();
    this.updateActiveState();
    this.autoExpandMenus();
    // Restore selected lab state from current route on initialization
    this.restoreSelectedLabFromRoute();
  }

  // A platform admin's lab context belongs to the header switcher. Opening a lab
  // page still filters this menu, but must not drop them out of Platform
  // overview by adopting that lab as their context.
  private routeCanSetLabContext(): boolean {
    return this.navigationService.currentUser()?.is_platform_admin !== true;
  }

  private selectLabAndFetchProjects(labId: number): void {
    // Only update if it's a different lab
    if (this.selectedLabId === labId) {
      // Same lab, just ensure Projects dropdown is expanded
      this.expandMenuItem('Projects');
      // Check if we need to update projects from already loaded lab detail
      const labDetail = this.labFacade.getLabDetailSignal();
      if (labDetail?.id === labId && labDetail.projects) {
        this.updateProjectsMenuItems(labDetail.projects);
      }
      return;
    }

    // Check if lab detail is already loaded before setting selectedLabId
    const labDetail = this.labFacade.getLabDetailSignal();
    const isLabDetailLoaded = labDetail?.id === labId && labDetail.projects;

    // Set selected lab ID
    this.selectedLabId = labId;

    // Set selected lab in state - try to get from lab list first, then from lab detail
    if (this.routeCanSetLabContext()) {
      const labList = this.labFacade.labListSignal();
      const labFromList = labList.find((lab) => lab.id === labId);
      if (labFromList) {
        this.labFacade.setSelectedLab(labFromList);
      } else if (labDetail?.id === labId) {
        // If lab detail is loaded, use that to set selected lab
        this.labFacade.setSelectedLab(labDetail);
      }
    }

    // Expand Projects dropdown when a lab is selected
    this.expandMenuItem('Projects');

    if (isLabDetailLoaded && labDetail.projects) {
      // Lab detail is already loaded for this lab, update projects immediately
      this.updateProjectsMenuItems(labDetail.projects);
    }
    // NOTE: We intentionally do NOT dispatch getLabDetail here. The
    // LabDetailsComponent (mounted at /labs/:id) is the single owner of
    // fetching lab detail and lab project users for the active lab route.
    // Having the sidenav also dispatch caused two concurrent fetches for the
    // newly-clicked lab on every nav, plus a stale fetch for the previously
    // selected lab in some interleavings. The sidenav still updates
    // selectedLabId / setSelectedLab synchronously above so other consumers
    // (e.g. the updateLabProjects$ effect) know which lab the user is on.
  }

  private restoreSelectedLabFromRoute(): void {
    this.currentRoute = this.router.url;
    // Check if we're on a lab route (but not a project route)
    const labRouteRegex = /^\/labs\/(\d+)$/;
    const labRouteMatch = labRouteRegex.exec(this.currentRoute);
    if (labRouteMatch) {
      const labId = Number.parseInt(labRouteMatch[1], 10);
      this.selectLabAndFetchProjects(labId);
    } else {
      // Check if we're on a project route - don't filter projects, just activate lab
      const projectRouteRegex = /^\/labs\/(\d+)\/project\/(\d+)/;
      const projectRouteMatch = projectRouteRegex.exec(this.currentRoute);
      if (projectRouteMatch) {
        // Don't set selectedLabId for project routes - we don't want to filter projects
        // The lab will be activated visually in handleProjectRoute
        // Expand Projects dropdown since we're viewing a project
        this.expandMenuItem('Projects');
      } else {
        // On other routes, clear selected lab
        this.selectedLabId = null;
      }
    }
  }

  private setupRouteSubscriptions(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.updateActiveState();
        this.autoExpandMenus();
        this.updateActiveStateForProjectRoute();
        // Only restore selected lab state if not already handled by route handlers
        // handleLabRoute() already calls selectLabAndFetchProjects, so we skip for lab routes
        const isLabRoute = /^\/labs\/(\d+)$/.test(this.router.url);
        if (!isLabRoute) {
          this.restoreSelectedLabFromRoute();
        }
      });
  }

  private setupNavigationFacadeSubscription(): void {
    this.navigationFacade.sideNavMenuItems$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((items) => {
      if (items.length > 0) {
        this.selectedMenuItem = items[0];
      }
    });
  }

  private setupEffects(): void {
    // The header switcher changes the application context for admins as well as
    // ordinary lab users, so rebuild the menu whenever the user or the selected
    // lab changes. The lab and project lists are read untracked, the effects
    // below already keep those submenus current.
    effect(() => {
      this.navigationService.currentUser();
      this.labFacade.selectedLabSignal();
      untracked(() => {
        this.sideNavMenuItems = this.navigationService.getAllMenuItems();
        this.updateLabsMenuItems(this.labFacade.labListSignal());
        this.updateProjectsMenuItems(this.projectFacade.projectListSignal());
        this.updateActiveState();
        this.autoExpandMenus();
      });
    });

    effect(() => {
      const labs = this.labFacade.labListSignal();
      this.updateLabsMenuItems(labs);
      this.updateActiveState();
      this.updateActiveStateForProjectRoute();
    });

    effect(() => {
      const projects = this.projectFacade.projectListSignal();
      this.allProjects = projects;
      // Only update with all projects if no lab is selected
      // If a lab is selected, let the lab detail effect handle the filtering
      if (this.selectedLabId === null) {
        this.updateProjectsMenuItems(projects);
        this.updateActiveState();
        this.updateActiveStateForProjectRoute();
      }
    });

    effect(() => {
      const labDetail = this.labFacade.getLabDetailSignal();
      const selectedLab = this.selectedLabId; // Read selectedLabId to ensure effect tracks it

      // Only update projects if we have a selected lab ID and the lab detail matches
      if (labDetail?.projects && selectedLab !== null && labDetail.id === selectedLab) {
        // Ensure selected lab is set in state when lab detail is loaded
        if (this.routeCanSetLabContext()) {
          this.labFacade.setSelectedLab(labDetail);
        }
        // Update projects menu items with lab-specific projects
        this.updateProjectsMenuItems(labDetail.projects);
      } else if (selectedLab === null) {
        // If no lab is selected, show all projects
        // This handles the case when we clear the selected lab
        const allProjects = this.projectFacade.projectListSignal();
        if (allProjects && allProjects.length > 0) {
          this.updateProjectsMenuItems(allProjects);
        }
      }
      // If selectedLab is set but labDetail doesn't match yet, wait for lab detail to load
    });

    effect(() => {
      const selectedProject = this.projectFacade.projectSelectedSignal();
      this.handleSelectedProjectChange(selectedProject);
    });
  }

  private handleSelectedProjectChange(selectedProject: IProject | null): void {
    if (!selectedProject?.lab) {
      return;
    }

    const labState = `labs/${selectedProject.lab}`;
    const labsMenuItem = this.sideNavMenuItems.find((item) => item.name === 'Labs');

    if (labsMenuItem?.sub) {
      const matchingLabSubItem = labsMenuItem.sub.find((sub) => sub.state === labState);
      if (matchingLabSubItem) {
        this.expandedMenu['Labs'] = true;
        this.scheduleScrollToLab(labState);
      }
    }
  }

  updateActiveState(): void {
    this.currentRoute = this.router.url;

    // Check routes in order of specificity: project > lab > general
    if (this.handleProjectRoute()) return;
    if (this.handleLabRoute()) return;
    this.handleGeneralRoute();
  }

  private handleProjectRoute(): boolean {
    const projectRouteRegex = /^\/labs\/(\d+)\/project\/(\d+)/;
    const projectRouteMatch = projectRouteRegex.exec(this.currentRoute);
    if (!projectRouteMatch) return false;

    const [, labIdStr, projectId] = projectRouteMatch;
    const labId = Number.parseInt(labIdStr, 10);
    const projectState = `labs/${labId}/project/${projectId}`;

    const projectsMenuItem = this.findMenuItemByName('Projects');
    const matchingProjectSubItem = projectsMenuItem?.sub?.find((sub) => sub.state === projectState);

    if (matchingProjectSubItem) {
      this.selectedMenuItem = matchingProjectSubItem;
      // Don't set selectedLabId for project routes - we don't want to filter projects
      // Only activate the lab visually
      const labState = `labs/${labId}`;
      const labsMenuItem = this.findMenuItemByName('Labs');
      const matchingLabSubItem = labsMenuItem?.sub?.find((sub) => sub.state === labState);
      if (matchingLabSubItem) {
        this.expandedMenu['Labs'] = true;
        this.scheduleScrollToLab(labState);
      }
      return true;
    }

    return false;
  }

  private handleLabRoute(): boolean {
    const labRouteRegex = /^\/labs\/(\d+)$/;
    const labRouteMatch = labRouteRegex.exec(this.currentRoute);
    if (!labRouteMatch) return false;

    const labId = Number.parseInt(labRouteMatch[1], 10);
    const labState = `labs/${labId}`;
    const labsMenuItem = this.findMenuItemByName('Labs');
    const matchingLabSubItem = labsMenuItem?.sub?.find((sub) => sub.state === labState);

    if (matchingLabSubItem) {
      this.selectedMenuItem = matchingLabSubItem;
      // Use the centralized method to select lab and fetch projects
      // This prevents duplicate API calls
      this.selectLabAndFetchProjects(labId);
      return true;
    }

    return false;
  }

  private handleGeneralRoute(): void {
    for (const item of this.sideNavMenuItems) {
      // Check sub-items first
      const matchingSubItem = item.sub?.find((sub) => sub.state && this.currentRoute.startsWith(`/${sub.state}`));
      if (matchingSubItem) {
        this.selectedMenuItem = matchingSubItem;
        return;
      }

      // Check parent item (exact match to avoid e.g. /labs matching /labs/1)
      if (item.state && this.currentRoute === `/${item.state}`) {
        this.selectedMenuItem = item;
        if (item.state === 'labs' || item.state === 'projects') {
          this.selectedLabId = null;
          this.updateProjectsMenuItems(this.allProjects);
        }
        return;
      }
    }

    // drop the previous selection
    this.selectedMenuItem = null;
  }

  private findMenuItemByName(name: string): IMenuItem | undefined {
    return this.sideNavMenuItems.find((item) => item.name === name);
  }

  autoExpandMenus(): void {
    this.currentRoute = this.router.url;

    const routeMenuMap: Array<{ pattern: RegExp | string; menuName: string }> = [
      { pattern: '/dashboard', menuName: 'Dashboard' },
      { pattern: '/labs', menuName: 'Labs' },
      { pattern: /\/projects|\/project\//, menuName: 'Projects' },
    ];

    for (const { pattern, menuName } of routeMenuMap) {
      const matches =
        typeof pattern === 'string' ? this.currentRoute.startsWith(pattern) : pattern.test(this.currentRoute);

      if (matches) {
        this.expandMenuItem(menuName);
      }
    }
  }

  private expandMenuItem(menuName: string): void {
    const item = this.findMenuItemByName(menuName);
    if (!item?.name) return;

    const isDropdown = item.type === MenuItemTypes.dropDown || item.type === MenuItemTypes.linkAndDropDown;
    if (isDropdown) {
      this.expandedMenu[item.name] = true;
    }
  }

  toggleDropdown(menuItem: IMenuItem): void {
    if (!menuItem.name) return;

    const isDropdown = menuItem.type === MenuItemTypes.dropDown || menuItem.type === MenuItemTypes.linkAndDropDown;
    if (isDropdown) {
      this.expandedMenu[menuItem.name] = !this.expandedMenu[menuItem.name];
    }
  }

  setSelectedNavItem(selectedItem: IMenuItem): void {
    this.selectedMenuItem = selectedItem;
    this.navigationFacade.setSideNavMenuItems([selectedItem]);
  }

  onSubItemClick(subItem: IMenuItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    // Check if this is a lab click
    const labStateMatch = subItem.state?.match(/^labs\/(\d+)$/);
    if (labStateMatch) {
      const labId = Number.parseInt(labStateMatch[1], 10);
      // Use the centralized method to select lab and fetch projects
      // This prevents duplicate API calls
      this.selectLabAndFetchProjects(labId);
    }

    // Check if this is a project click
    const projectStateMatch = subItem.state?.match(/^labs\/(\d+)\/project\/(\d+)$/);
    if (projectStateMatch) {
      const labId = Number.parseInt(projectStateMatch[1], 10);
      // Don't set selectedLabId for project clicks - we don't want to filter projects
      // Only activate the lab visually
      const labState = `labs/${labId}`;
      const labsMenuItem = this.findMenuItemByName('Labs');
      const matchingLabSubItem = labsMenuItem?.sub?.find((sub) => sub.state === labState);
      if (matchingLabSubItem) {
        this.expandedMenu['Labs'] = true;
        this.scheduleScrollToLab(labState);
      }
    }

    if (subItem.state) {
      this.router.navigate([`/${subItem.state}`]);
    }
  }

  updateLabsMenuItems(labs: ILab[]): void {
    // Guard against undefined/null labs
    if (!labs) {
      return;
    }

    // Filter to only show labs with SUCCESS build_status
    const successfulLabs = labs.filter((lab) => lab.build_status === 'SUCCESS');

    this.sideNavMenuItems = this.sideNavMenuItems.map((item) => {
      // Only the admin all-labs dropdown gets sub-items injected. Lab-scoped
      // users have no 'Labs' item, so this is a no-op for them.
      if (item.name === 'Labs' && item.type === MenuItemTypes.linkAndDropDown) {
        return {
          ...item,
          sub: successfulLabs.map((lab) => this.createLabMenuItem(lab)),
        };
      }
      return item;
    });

    // Scroll to active lab after labs are updated
    const activeLabState = this.getActiveLabFromRoute();
    if (activeLabState && this.expandedMenu['Labs']) {
      this.scheduleScrollToLab(activeLabState);
    }
  }

  updateProjectsMenuItems(projects: IProject[]): void {
    // Guard against undefined/null projects
    if (!projects) {
      return;
    }

    // Filter to only show projects with SUCCESS build_status
    const successfulProjects = projects.filter((project) => project.build_status === 'SUCCESS');

    this.sideNavMenuItems = this.sideNavMenuItems.map((item) => {
      // Only the admin all-projects dropdown gets sub-items injected. The
      // lab-scoped 'Projects' entry is a plain link and is left untouched.
      if (item.name === 'Projects' && item.type === MenuItemTypes.linkAndDropDown) {
        return {
          ...item,
          sub: successfulProjects.map((project) => this.createProjectMenuItem(project)),
        };
      }
      return item;
    });
  }

  private createLabMenuItem(lab: ILab): IMenuItem {
    return {
      name: lab.display_name || `Lab ${lab.id}`,
      type: MenuItemTypes.link,
      state: `labs/${lab.id}`,
    };
  }

  private createProjectMenuItem(project: IProject): IMenuItem {
    return {
      name: project.display_name || `Project ${project.id}`,
      type: MenuItemTypes.link,
      state: `labs/${project.lab}/project/${project.id}`,
    };
  }

  isSelected(menuItem: IMenuItem): boolean {
    // Check if this exact item is selected
    if (this.selectedMenuItem?.state === menuItem.state) {
      return true;
    }

    // Special case: highlight lab when viewing its project
    const activeLabState = this.getActiveLabFromRoute();
    if (activeLabState && menuItem.state === activeLabState) {
      return true;
    }

    return false;
  }

  isParentActive(menuItem: IMenuItem): boolean {
    // Lab-scoped entries highlight when their page is open for the active lab.
    if (menuItem.labScopedSection) {
      return isLabSectionActive(this.router.url, this.labFacade.selectedLabSignal()?.id, menuItem.labScopedSection);
    }

    // Check if item itself is selected
    if (this.isSelected(menuItem)) {
      return true;
    }

    // Check if current route matches item or any child
    if (this.matchesCurrentRoute(menuItem)) {
      return true;
    }

    // Check if any child route matches current route
    if (this.hasActiveSubItem(menuItem)) {
      return true;
    }

    return false;
  }

  private matchesCurrentRoute(menuItem: IMenuItem): boolean {
    if (!menuItem.state) return false;
    const path = `/${menuItem.state}`;
    return menuItem.exactRoute ? this.currentRoute === path : this.currentRoute.startsWith(path);
  }

  private hasActiveSubItem(menuItem: IMenuItem): boolean {
    return !!menuItem.sub?.some((sub) => sub.state && this.currentRoute.startsWith(`/${sub.state}`));
  }

  private getActiveLabFromRoute(): string | null {
    const match = /^\/labs\/(\d+)\/project\/(\d+)/.exec(this.currentRoute);
    return match ? `labs/${match[1]}` : null;
  }

  private updateActiveStateForProjectRoute(): void {
    const projectRouteRegex = /^\/labs\/(\d+)\/project\/(\d+)/;
    const projectRouteMatch = projectRouteRegex.exec(this.currentRoute);
    if (!projectRouteMatch) return;

    const labId = projectRouteMatch[1];
    const labState = `labs/${labId}`;
    const labsMenuItem = this.findMenuItemByName('Labs');
    const matchingLabSubItem = labsMenuItem?.sub?.find((sub) => sub.state === labState);

    if (matchingLabSubItem) {
      this.expandedMenu['Labs'] = true;
      this.scheduleScrollToLab(labState);
    }
  }

  handleMenuItemClick(menuItem: IMenuItem): void {
    // Handle parent Lab click - show all labs and all projects
    if (menuItem.name === 'Labs' && menuItem.state === 'labs') {
      this.selectedLabId = null;
      // Reset projects to show all projects
      this.updateProjectsMenuItems(this.allProjects);
    }

    // Handle parent Project click - show all projects
    if (menuItem.name === 'Projects' && menuItem.state === 'projects') {
      this.selectedLabId = null;
      // Reset projects to show all projects
      this.updateProjectsMenuItems(this.allProjects);
    }

    // Handle pure dropdown - toggle only
    if (menuItem.type === MenuItemTypes.dropDown) {
      this.toggleDropdown(menuItem);
      return;
    }

    // Handle linkAndDropDown and link types - navigate only (do not set selectedMenuItem here)
    if (menuItem.state && this.currentRoute !== `/${menuItem.state}`) {
      this.router.navigate([`/${menuItem.state}`]);
    }
  }

  // Template helper methods
  openDialog(menuItem: IMenuItem): void {
    if (menuItem.dialogId) {
      this.dialogService.open(menuItem.dialogId);
    }
  }

  isStandardMenuItem(menuItem: IMenuItem): boolean {
    return (
      menuItem.type === MenuItemTypes.linkAndDropDown ||
      menuItem.type === MenuItemTypes.dropDown ||
      menuItem.type === MenuItemTypes.link
    );
  }

  isDropdownType(menuItem: IMenuItem): boolean {
    return menuItem.type === MenuItemTypes.dropDown || menuItem.type === MenuItemTypes.linkAndDropDown;
  }

  isExpanded(menuItem: IMenuItem): boolean {
    return menuItem.name ? this.expandedMenu[menuItem.name] : false;
  }

  getRouterLink(menuItem: IMenuItem): (string | number)[] {
    // Lab-scoped entries (Lab/Sequences/Projects/Users) resolve against the
    // active lab at render time, so switching labs retargets them without
    // rebuilding the menu.
    if (menuItem.labScopedSection) {
      return labSectionLink(menuItem.labScopedSection, this.labFacade.selectedLabSignal()?.id);
    }
    const isLink = menuItem.type === MenuItemTypes.linkAndDropDown || menuItem.type === MenuItemTypes.link;
    return isLink && menuItem.state ? ['/', menuItem.state] : [];
  }

  isTopLevelRoute(menuItem: IMenuItem): boolean {
    if (menuItem.type === MenuItemTypes.link) return true;
    if (menuItem.type === MenuItemTypes.linkAndDropDown) {
      return this.currentRoute === '/dashboard' || this.currentRoute === '/labs' || this.currentRoute === '/projects';
    }
    return false;
  }

  shouldShowSubmenu(menuItem: IMenuItem): boolean {
    return !!(menuItem.name && this.expandedMenu[menuItem.name] && menuItem.sub);
  }

  isScrollableSubmenu(menuItem: IMenuItem): boolean {
    return (menuItem.name === 'Labs' || menuItem.name === 'Projects') && !!menuItem.sub && menuItem.sub.length > 5;
  }

  isValidSubItem(subItem: IMenuItem): boolean {
    return subItem.type !== MenuItemTypes.header && !!subItem.state;
  }

  private scheduleScrollToLab(labState: string): void {
    // Prevent multiple scrolls to the same lab
    if (this.lastScrolledLabState === labState) {
      return;
    }

    // Clear any pending scroll
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Schedule a new scroll
    this.scrollTimeout = setTimeout(() => {
      this.scrollToActiveLab(labState);
      this.lastScrolledLabState = labState;
      this.scrollTimeout = null;
    }, 150);
  }

  private scrollToActiveLab(labState: string): void {
    // Find the active lab link element by data attribute
    const activeLabLink = document.querySelector(`a[data-lab-state="${labState}"]`) as HTMLElement;
    if (!activeLabLink) {
      return;
    }

    // Find the scrollable container (parent ul with labs-submenu-scrollable class)
    const scrollableContainer = activeLabLink.closest('ul.labs-submenu-scrollable') as HTMLElement;
    if (!scrollableContainer) {
      return;
    }

    // Get the parent li element for better positioning
    const listItem = activeLabLink.closest('li') as HTMLElement;
    if (!listItem) {
      return;
    }

    // Calculate scroll position to center the active item
    const containerHeight = scrollableContainer.clientHeight;
    const listItemOffsetTop = listItem.offsetTop;
    const listItemHeight = listItem.offsetHeight;

    // Scroll to center the active item in the visible area
    const targetScrollTop = listItemOffsetTop - containerHeight / 2 + listItemHeight / 2;

    scrollableContainer.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior: 'smooth',
    });
  }
}
